import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Calculator, Factory, History, Layers, Link2, Loader2, Package, Pencil, Plus, RefreshCw, Search, Send, Share2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { EmptyState, ErrorState, Field, Loading, PageHeader, Surface, Thumb } from "@/components/ds";
import { typeLabel } from "@/components/products/CustomFieldsLibrary";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ImageUpload } from "@/components/ds/ImageUpload";
import { ProductHistoryModal, ProductStockModal } from "@/components/ds/ProductStock";

/** Busca sem acento e sem diferenciar maiúsculas. */
const norm = (v) =>
  (v || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
import { KitModal, KitVisual } from "@/components/ds/KitModal";
import { CostRefreshModal } from "@/components/products/CostRefreshModal";

const Badge = ({ tone, children, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
      {
        on: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
        off: "border-white/15 bg-secondary/60 text-muted-foreground",
        kit: "border-brand/40 bg-brand/15 text-brand",
      }[tone]
    }`}
  >
    {children}
  </span>
);

const EMPTY = {
  name: "",
  code: "",
  description: "",
  category: "",
  cost: "",
  price: "",
  consign_price: "",
  reseller_price: "",
  photo_path: null,
  show_in_catalog: true,
  kit_eligible: true,
  featured: false,
};

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [platesFor, setPlatesFor] = useState(null);
  const [stockFor, setStockFor] = useState(null);
  const [costFor, setCostFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [filaments, setFilaments] = useState([]);
  const [cats, setCats] = useState([]);
  const [creatorList, setCreatorList] = useState([]);
  const [kits, setKits] = useState([]);
  const [editingKit, setEditingKit] = useState(null);
  const [kitToDelete, setKitToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [clearingUnassigned, setClearingUnassigned] = useState(null);
  const [sharing, setSharing] = useState(null);

  const confirmClearUnassigned = async () => {
    try {
      await api.post(`/products/${clearingUnassigned.id}/stock/clear-unassigned`);
      toast.success("Saldo sem cor definida zerado. As cores não foram afetadas.");
      setClearingUnassigned(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setClearingUnassigned(null);
    }
  };

  const load = useCallback(async () => {
    setError("");
    try {
      const [p, k, m, c, f] = await Promise.all([
        api.get("/products"),
        api.get("/kits"),
        api.get("/materials"),
        api.get("/categories"),
        api.get("/filaments"),
      ]);
      setProducts(p.data);
      setKits(k.data);
      setMaterials(m.data);
      setCats(c.data);
      setFilaments(f.data);
      api
        .get("/creators")
        .then(({ data }) => setCreatorList(data?.creators || []))
        .catch(() => setCreatorList([]));
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${toDelete.id}`);
      toast.success("Produto excluído.");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setToDelete(null);
    }
  };

  const confirmDeleteKit = async () => {
    try {
      await api.delete(`/kits/${kitToDelete.id}`);
      toast.success("Kit excluído. Os produtos continuam cadastrados.");
      setKitToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setKitToDelete(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="products-page">
      <PageHeader
        title="Produtos"
        subtitle="Cadastre o que você vende, escolha o que aparece no catálogo e monte kits."
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/calculadora")}
              data-testid="open-calculator-btn"
              className="h-11 gap-2 rounded-full px-4"
            >
              <Calculator className="h-4 w-4" /> Calculadora
            </Button>
            <Button
              variant="outline"
              onClick={() => setEditingKit("new")}
              data-testid="new-kit-btn"
              className="h-11 gap-2 rounded-full px-4"
            >
              <Boxes className="h-4 w-4" /> Novo Kit
            </Button>
            <Button
              onClick={() => setEditing("new")}
              data-testid="new-product-btn"
              className="h-11 gap-2 rounded-full px-5 font-semibold transition-transform active:scale-95"
            >
              <Plus className="h-4 w-4" /> Novo Produto
            </Button>
          </div>
        }
      />

      {products === null && !error && <Loading label="Carregando seus produtos..." />}
      {error && <ErrorState message={error} onRetry={load} />}

      {products && (
        <Tabs defaultValue="produtos">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <TabsList className="bg-secondary/60">
              <TabsTrigger value="produtos" data-testid="tab-products">
                Produtos ({products.length})
              </TabsTrigger>
              <TabsTrigger value="kits" data-testid="tab-kits">
                Kits ({kits.length})
              </TabsTrigger>
            </TabsList>
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar produto..."
                data-testid="products-search-input"
                className="h-10 rounded-full pl-9"
              />
            </div>
          </div>

          <TabsContent value="produtos" className="mt-6 space-y-6">
            {renderProducts()}
          </TabsContent>

          <TabsContent value="kits" className="mt-6 space-y-6">
            {kits.length === 0 ? (
              <EmptyState
                icon={Boxes}
                title="Nenhum kit criado"
                description="Um kit reúne produtos que você já cadastrou, com preço próprio, sem duplicar nada."
                action={
                  <Button onClick={() => setEditingKit("new")} data-testid="empty-new-kit-btn" className="gap-2 rounded-full">
                    <Plus className="h-4 w-4" /> Novo Kit
                  </Button>
                }
              />
            ) : (
              <div className="grid gap-4 stagger md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
                {kits.map((k) => (
                  <Surface key={k.id} className="p-5" data-testid={`kit-card-${k.id}`}>
                    <div className="flex items-start gap-4">
                      <KitVisual photoPath={k.photo_path} components={k.components} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-display text-base font-medium" data-testid={`kit-name-${k.id}`}>
                          {k.name}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          <Badge tone="kit">Kit</Badge>
                          {k.show_in_catalog !== false && k.active !== false ? (
                            <Badge tone="on" testid={`kit-badge-catalog-${k.id}`}>Catálogo</Badge>
                          ) : (
                            <Badge tone="off" testid={`kit-badge-hidden-${k.id}`}>Oculto</Badge>
                          )}
                        </div>
                        <div className="mt-3 flex items-baseline gap-3">
                          <span className="font-num text-xl font-medium text-brand" data-testid={`kit-price-${k.id}`}>
                            {brl(k.price)}
                          </span>
                          <span className="font-num text-xs text-muted-foreground">custo {brl(k.cost)}</span>
                        </div>
                      </div>
                    </div>

                    <ul className="mt-4 space-y-1.5 rounded-lg border border-white/10 bg-secondary/40 p-3 text-sm">
                      {k.components.map((c) => (
                        <li key={c.product_id} className="flex items-center gap-2">
                          <span className="font-num text-xs text-muted-foreground">{c.quantity}x</span>
                          <span className="truncate">{c.name}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-3 flex justify-end gap-1 border-t border-white/10 pt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSharing(k)}
                        data-testid={`kit-share-btn-${k.id}`}
                        className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Share2 className="h-3.5 w-3.5" /> Compartilhar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingKit(k)}
                        data-testid={`kit-edit-btn-${k.id}`}
                        className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setKitToDelete(k)}
                        data-testid={`kit-delete-btn-${k.id}`}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </Surface>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {editingKit && (
        <KitModal
          kit={editingKit === "new" ? null : editingKit}
          products={products || []}
          categories={cats || []}
          creators={creatorList}
          onClose={() => setEditingKit(null)}
          onSaved={load}
        />
      )}

      <AlertDialog open={!!kitToDelete} onOpenChange={(o) => !o && setKitToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-kit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir kit?</AlertDialogTitle>
            <AlertDialogDescription>
              O kit <strong>{kitToDelete?.name}</strong> será removido. Os produtos que fazem parte
              dele continuam cadastrados normalmente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-kit-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteKit}
              data-testid="confirm-delete-kit-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!clearingUnassigned} onOpenChange={(o) => !o && setClearingUnassigned(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="clear-unassigned-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Zerar saldo sem cor definida?</AlertDialogTitle>
            <AlertDialogDescription>
              As{" "}
              <strong className="font-num">
                {Number(clearingUnassigned?.stock_unassigned || 0).toLocaleString("pt-BR")}
              </strong>{" "}
              un antigas de <strong>{clearingUnassigned?.name}</strong> que ficaram sem cor serão
              zeradas com um ajuste de estoque. O saldo de cada cor cadastrada continua igual, e o
              ajuste fica registrado no histórico do produto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-clear-unassigned-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmClearUnassigned}
              data-testid="confirm-clear-unassigned-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Zerar saldo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editing && (
        <ProductModal
          product={editing === "new" ? null : editing}
          materials={materials}
          filaments={filaments}
          cats={cats}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}

      {platesFor && (
        <ProductPlatesModal
          product={platesFor}
          onClose={() => setPlatesFor(null)}
          onNewPlate={() => navigate(`/plates?product=${platesFor.id}&new=1`)}
          onEditPlate={(plate) => navigate(`/plates?product=${plate.product_id}`)}
        />
      )}

      {stockFor && (
        <ProductStockModal product={stockFor} onClose={() => setStockFor(null)} onSaved={load} />
      )}
      {costFor && (
        <CostRefreshModal product={costFor} onClose={() => setCostFor(null)} onSaved={load} />
      )}
      {historyFor && <ProductHistoryModal product={historyFor} onClose={() => setHistoryFor(null)} />}

      {sharing && <ShareProductModal product={sharing} onClose={() => setSharing(null)} />}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-product-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir produto?</AlertDialogTitle>
            <AlertDialogDescription>
              O produto <strong>{toDelete?.name}</strong> será removido. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-product-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="confirm-delete-product-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  function renderProducts() {
    if (products.length === 0)
      return (
        <EmptyState
          icon={Package}
          title="Nenhum produto cadastrado"
          description="Comece cadastrando seu primeiro produto."
          action={
            <Button onClick={() => setEditing("new")} data-testid="empty-new-product-btn" className="gap-2 rounded-full">
              <Plus className="h-4 w-4" /> Novo Produto
            </Button>
          }
        />
      );

    const term = norm(search);
    const visible = term
      ? products.filter((p) =>
          [p.name, p.code, ...(p.variations || []).map((v) => v.sku)]
            .filter(Boolean)
            .some((field) => norm(field).includes(term))
        )
      : products;

    if (visible.length === 0)
      return (
        <EmptyState
          icon={Search}
          title="Nenhum item encontrado."
          description="Tente outro nome ou SKU."
        />
      );

    return (
      <div className="grid gap-4 stagger md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
        {visible.map((p) => (
          <Surface key={p.id} className="p-5" data-testid={`product-card-${p.id}`}>
            <div className="flex items-start gap-4">
              <Thumb
                src={fileUrl(p.photo_path)}
                alt={p.name}
                className="h-16 w-16"
                fallback={<Package className="h-5 w-5 opacity-70" />}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-medium" data-testid={`product-name-${p.id}`}>
                  {p.name}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {[p.code, p.category].filter(Boolean).join(" · ") || "Sem código"}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {p.show_in_catalog !== false && p.active !== false ? (
                    <Badge tone="on" testid={`product-badge-catalog-${p.id}`}>Catálogo</Badge>
                  ) : (
                    <Badge tone="off" testid={`product-badge-hidden-${p.id}`}>Oculto</Badge>
                  )}
                  {p.kit_eligible !== false && <Badge tone="kit">Pode ir em kit</Badge>}
                  {p.featured && <Badge tone="kit" testid={`product-badge-featured-${p.id}`}>Destaque</Badge>}
                  {(p.variations || []).length > 0 && (
                    <Badge tone="kit" testid={`product-badge-variations-${p.id}`}>
                      {p.variations.length} {p.variations.length === 1 ? "combinação" : "combinações"}
                    </Badge>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap items-baseline gap-3">
                  <span className="font-num text-xl font-medium text-brand" data-testid={`product-price-${p.id}`}>
                    {brl(p.price)}
                  </span>
                  <span className="font-num text-xs text-muted-foreground">custo {brl(p.cost)}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCostFor(p)}
                    data-testid={`product-cost-refresh-btn-${p.id}`}
                    className="h-7 gap-1.5 rounded-full px-3 text-[11px]"
                  >
                    <RefreshCw className="h-3 w-3" /> Atualizar custo
                  </Button>
                </div>
              </div>
            </div>
            {p.description && (
              <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">{p.description}</p>
            )}

            <div className="mt-4 flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5">
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <Boxes className="h-3.5 w-3.5" /> Em estoque
              </span>
              <div className="flex items-center gap-2">
                <span className="font-num text-sm" data-testid={`product-stock-${p.id}`}>
                  {Number(p.stock_units || 0).toLocaleString("pt-BR")} un
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStockFor(p)}
                  data-testid={`product-stock-btn-${p.id}`}
                  className="h-7 rounded-full px-3 text-[11px]"
                >
                  Movimentar
                </Button>
              </div>
            </div>

            {(p.variations || []).length > 0 && (
              <ul className="mt-2 space-y-1 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2" data-testid={`product-variations-stock-${p.id}`}>
                {p.variations.map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate text-muted-foreground">{v.name}</span>
                    <span className="font-num" data-testid={`product-variation-stock-${v.id}`}>
                      {Number(v.stock_units || 0).toLocaleString("pt-BR")} un
                    </span>
                  </li>
                ))}
                {Number(p.stock_unassigned || 0) !== 0 && (
                  <li
                    className="flex items-center justify-between gap-2 border-t border-white/10 pt-1 text-xs text-warning"
                    data-testid={`product-stock-unassigned-${p.id}`}
                  >
                    <span className="truncate">Sem cor definida</span>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-num">
                        {Number(p.stock_unassigned).toLocaleString("pt-BR")} un
                      </span>
                      {Number(p.stock_unassigned) > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setClearingUnassigned(p)}
                          data-testid={`clear-unassigned-btn-${p.id}`}
                          className="h-6 rounded-full px-2 text-[11px] text-muted-foreground hover:text-destructive"
                        >
                          Zerar
                        </Button>
                      )}
                    </div>
                  </li>
                )}
              </ul>
            )}

            <div className="mt-3 flex flex-wrap justify-end gap-1 border-t border-white/10 pt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPlatesFor(p)}
                data-testid={`product-plates-btn-${p.id}`}
                className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <Layers className="h-3.5 w-3.5" /> Plates
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/calculadora?product=${p.id}`)}
                data-testid={`product-pricing-btn-${p.id}`}
                className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <Calculator className="h-3.5 w-3.5" /> Precificar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHistoryFor(p)}
                data-testid={`product-history-btn-${p.id}`}
                className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <History className="h-3.5 w-3.5" /> Histórico
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSharing(p)}
                data-testid={`product-share-btn-${p.id}`}
                className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <Share2 className="h-3.5 w-3.5" /> Compartilhar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(p)}
                data-testid={`product-edit-btn-${p.id}`}
                className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <Pencil className="h-3.5 w-3.5" /> Editar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setToDelete(p)}
                data-testid={`product-delete-btn-${p.id}`}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Surface>
        ))}
      </div>
    );
  }
}

/** Link público do produto e de cada combinação: slug + SKU, sem id interno. */
const publicSlug = (name) =>
  (name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "produto";

function ShareProductModal({ product, onClose }) {
  // Lista exatamente o que está publicado: as combinações vêm do catálogo público
  // (kits com combinações herdadas incluídas).
  const [rows, setRows] = useState(null);
  const base = `${window.location.origin}/catalogo/p/${publicSlug(product.name)}`;

  useEffect(() => {
    api
      .get("/public/catalog")
      .then(({ data }) => {
        const row = data.find((x) => x.id === product.id);
        setRows([
          {
            label: row?.kind === "kit" ? "Kit (todas as combinações)" : "Produto (todas as combinações)",
            url: base,
            published: !!row,
          },
          ...((row?.variations || product.variations || [])
            .filter((v) => v.active !== false && v.show_in_catalog !== false)
            .map((v) => ({
              label: v.name,
              url: `${base}?c=${encodeURIComponent(v.sku || v.id)}`,
              published: !!row,
            }))),
        ]);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [product.id]);

  const share = async (row, mode) => {
    if (mode === "copy") {
      await navigator.clipboard.writeText(row.url);
      toast.success("Link copiado.");
      return;
    }
    const text = `Olha esse: ${product.name}${
      row.label.includes("todas as combinações") ? "" : ` · ${row.label}`
    }\n${row.url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Compartilhar"
      description="Envie o link direto já com a combinação escolhida. Só aparece o que está publicado no catálogo."
      testid="share-product-modal"
    >
      <div className="mt-2 w-full min-w-0 space-y-2">
        {rows === null && <p className="text-sm text-muted-foreground">Carregando links...</p>}
        {rows?.length > 0 && !rows[0].published && (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
            Este item está fora do catálogo público: o link não vai abrir para o cliente.
          </p>
        )}
        {(rows || []).map((row) => (
          <div
            key={row.url}
            className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-white/10 bg-secondary/40 p-3"
            data-testid={`share-row-${publicSlug(row.label)}`}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{row.label}</p>
              <p className="truncate text-[11px] text-muted-foreground">{row.url}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => share(row, "copy")}
              data-testid={`share-copy-${publicSlug(row.label)}`}
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
            >
              <Link2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => share(row, "whatsapp")}
              data-testid={`share-whatsapp-${publicSlug(row.label)}`}
              className="h-8 px-2 text-brand"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </ResponsiveModal>
  );
}


function ProductModal({ product, materials, filaments, cats, onClose, onSaved }) {
  const [form, setForm] = useState(
    product
      ? {
          ...product,
          cost: String(product.cost ?? ""),
          price: String(product.price ?? ""),
          consign_price: String(product.consign_price ?? ""),
          reseller_price: String(product.reseller_price ?? ""),
        }
      : EMPTY
  );
  const [bom, setBom] = useState(
    (product?.materials || []).map((m) => ({
      material_id: m.material_id,
      quantity_per_unit: String(m.quantity_per_unit ?? 1),
    }))
  );
  const [pick, setPick] = useState("");
  const [vars, setVars] = useState(
    (product?.variations || []).map((v) => ({
      id: v.id,
      name: v.name,
      sku: v.sku || "",
      photo_path: v.photo_path || null,
      colors: (v.colors || []).map((c) => ({ name: c.name, filament_id: c.filament_id })),
      active: v.active !== false,
      show_in_catalog: v.show_in_catalog !== false,
      stock_units: v.stock_units || 0,
    }))
  );
  const [catId, setCatId] = useState(product?.category_id || "");
  const [catSearch, setCatSearch] = useState("");
  const [newCat, setNewCat] = useState("");
  const [catList, setCatList] = useState(cats || []);
  const [creatingCat, setCreatingCat] = useState(false);

  const createCategory = async () => {
    const name = newCat.trim();
    if (name.length < 2) return toast.error("Digite o nome da categoria.");
    setCreatingCat(true);
    try {
      const { data } = await api.post("/categories", { name });
      setCatList((prev) => [...prev, data]);
      setCatId(data.id);
      setNewCat("");
      setCatSearch("");
      toast.success(`Categoria "${data.name}" criada e selecionada.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreatingCat(false);
    }
  };
  const [busy, setBusy] = useState(false);
  const [customizable, setCustomizable] = useState(product?.customizable === true);
  const [requireApproval, setRequireApproval] = useState(
    product?.require_customization_approval === true
  );
  const [customFields, setCustomFields] = useState(product?.custom_fields || []);
  const [library, setLibrary] = useState([]);
  const [creators, setCreators] = useState([]);
  const [creatorId, setCreatorId] = useState(product?.creator_id || "");

  useEffect(() => {
    api
      .get("/custom-fields")
      .then(({ data }) => setLibrary(data))
      .catch(() => {});
    api
      .get("/creators")
      .then(({ data }) => setCreators((data?.creators || []).filter((c) => c.active !== false)))
      .catch(() => {});
  }, []);

  const toggleLibraryField = (f) => {
    setCustomFields((prev) =>
      prev.some((x) => x.name === f.name)
        ? prev.filter((x) => x.name !== f.name)
        : [
            ...prev,
            {
              name: f.name,
              type: f.type,
              required: f.required !== false,
              max_length: f.max_length || 40,
              options: f.options || [],
              filament_ids: f.filament_ids || [],
            },
          ]
    );
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const matById = Object.fromEntries((materials || []).map((m) => [m.id, m]));
  const addMaterial = (id) => {
    if (!id) return;
    if (bom.some((b) => b.material_id === id)) {
      toast.error("Este material já está na lista.");
      return;
    }
    setBom((prev) => [...prev, { material_id: id, quantity_per_unit: "1" }]);
    setPick("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (bom.some((b) => !Number(b.quantity_per_unit) || Number(b.quantity_per_unit) <= 0)) {
      toast.error("Informe a quantidade de cada material por unidade.");
      return;
    }
    if (vars.some((v) => !v.name.trim())) {
      toast.error("Dê um nome para cada combinação de cores.");
      return;
    }
    if (vars.some((v) => v.colors.some((c) => !c.name.trim() || !c.filament_id))) {
      toast.error("Cada cor da combinação precisa de nome e filamento.");
      return;
    }
    setBusy(true);
    const body = {
      name: form.name,
      description: form.description || "",
      category: form.category || "",
      photo_path: form.photo_path || null,
      category_id: catId || null,
      creator_id: creatorId || null,
      cost: Number(form.cost || 0),
      price: Number(form.price || 0),
      consign_price: Number(form.consign_price || 0),
      reseller_price: Number(form.reseller_price || 0),
      active: form.active !== false,
      show_in_catalog: form.show_in_catalog !== false,
      kit_eligible: form.kit_eligible !== false,
      featured: form.featured === true,
      customizable: customizable,
      require_customization_approval: customizable && requireApproval,
      custom_fields: customizable ? customFields : [],
      variations: vars.map((v) => ({
        id: v.id || null,
        name: v.name.trim(),
        photo_path: v.photo_path || null,
        colors: v.colors.map((c) => ({ name: c.name.trim(), filament_id: c.filament_id })),
        active: v.active !== false,
        show_in_catalog: v.show_in_catalog !== false,
      })),
      materials: bom.map((b) => ({
        material_id: b.material_id,
        quantity_per_unit: Number(b.quantity_per_unit),
      })),
    };
    try {
      if (product) await api.put(`/products/${product.id}`, body);
      else await api.post("/products", body);
      toast.success(product ? "Produto atualizado com sucesso." : "Produto cadastrado com sucesso.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={product ? "Editar Produto" : "Novo Produto"}
      testid="product-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <ImageUpload
          value={form.photo_path}
          onChange={(p) => setForm((f) => ({ ...f, photo_path: p }))}
          testid="product-image-upload"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome">
            <Input value={form.name} onChange={set("name")} required placeholder="Ex.: Chaveiro Gato" data-testid="product-name-input" />
          </Field>
          <Field label="Código" hint="Gerado automaticamente pelo sistema.">
            <Input
              value={form.code || ""}
              readOnly
              disabled
              placeholder="Ex.: CM001 (gerado ao salvar)"
              className="font-num"
              data-testid="product-code-input"
            />
          </Field>
          <Field label="Categoria">
            <div className="space-y-2" data-testid="product-category-picker">
              {catList.length > 4 && (
                <Input
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  placeholder="Buscar categoria..."
                  className="h-9"
                  data-testid="product-category-search"
                />
              )}
              <NativeSelect
                value={catId}
                onChange={setCatId}
                placeholder="Sem categoria"
                data-testid="product-category-select"
                options={catList
                  .filter(
                    (c) =>
                      !catSearch.trim() ||
                      c.name.toLowerCase().includes(catSearch.trim().toLowerCase())
                  )
                  .map((c) => ({ value: c.id, label: c.name }))}
              />
              <div className="flex gap-2">
                <Input
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  placeholder="Criar nova categoria"
                  className="h-9"
                  data-testid="product-new-category-input"
                />
                <Button
                  type="button"
                  variant="soft"
                  onClick={createCategory}
                  disabled={creatingCat}
                  data-testid="product-create-category-btn"
                  className="h-9 shrink-0 rounded-full px-3 text-xs"
                >
                  {creatingCat ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Criar"}
                </Button>
              </div>
            </div>
          </Field>
          <Field
            label="Criadora responsável"
            hint="Opcional. Vincular não aprova o produto: a liberação continua sendo manual."
          >
            <NativeSelect
              value={creatorId}
              onChange={setCreatorId}
              placeholder="Sem criadora"
              data-testid="product-creator-select"
              options={creators.map((c) => ({ value: c.id, label: c.display_name }))}
            />
          </Field>
          <Field label="Custo (R$)">
            <Input type="number" min="0" step="0.01" value={form.cost} onChange={set("cost")} className="font-num" data-testid="product-cost-input" />
          </Field>
          <Field label="Preço público (R$)" className="sm:col-span-2">
            <Input type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="font-num" data-testid="product-price-input" />
          </Field>
          <Field label="Preço consignado (R$)">
            <Input type="number" min="0" step="0.01" value={form.consign_price} onChange={set("consign_price")} className="font-num" data-testid="product-consign-price-input" />
          </Field>
          <Field label="Preço lojista (R$)">
            <Input type="number" min="0" step="0.01" value={form.reseller_price} onChange={set("reseller_price")} className="font-num" data-testid="product-reseller-price-input" />
          </Field>
        </div>
        <Field label="Descrição">
          <Textarea value={form.description} onChange={set("description")} rows={2} data-testid="product-description-input" />
        </Field>

        <div className="space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-4" data-testid="product-materials-section">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Materiais necessários
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Argola, embalagem e afins usados por unidade. A Plate cuida do filamento; estes
              materiais entram no custo e saem do estoque quando a produção é concluída.
            </p>
          </div>

          {(materials || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Cadastre materiais em Estoque → Materiais e insumos para usá-los aqui.
            </p>
          ) : (
            <NativeSelect
              value={pick}
              onChange={addMaterial}
              placeholder="Adicionar material..."
              options={(materials || [])
                .filter((m) => !bom.some((b) => b.material_id === m.id))
                .map((m) => ({ value: m.id, label: `${m.name} (${m.unit})` }))}
              data-testid="product-material-picker"
            />
          )}

          {bom.length > 0 && (
            <ul className="space-y-2">
              {bom.map((b, idx) => (
                <li
                  key={b.material_id}
                  className="flex items-center gap-3 rounded-md border border-white/10 bg-card/60 p-2.5"
                  data-testid={`product-material-row-${b.material_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{matById[b.material_id]?.name || "Material removido"}</p>
                    <p className="text-xs text-muted-foreground">
                      por unidade · {matById[b.material_id]?.unit || "UN"}
                    </p>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={b.quantity_per_unit}
                    onChange={(ev) =>
                      setBom((prev) =>
                        prev.map((p, i) =>
                          i === idx ? { ...p, quantity_per_unit: ev.target.value } : p
                        )
                      )
                    }
                    className="h-9 w-20 font-num"
                    data-testid={`product-material-qty-${b.material_id}`}
                  />
                  <button
                    type="button"
                    onClick={() => setBom((prev) => prev.filter((_, i) => i !== idx))}
                    data-testid={`product-material-remove-${b.material_id}`}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-4" data-testid="product-variations-section">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Cores / combinações
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Uma combinação é UMA unidade comercial: "Rosa + Branco" é uma variação com 2 cores.
              O preço é sempre do produto. Cada combinação tem SKU gerado pelo sistema, foto,
              estoque próprio e o filamento de cada cor. As cores entram nos filamentos da Plate
              pela ordem de consumo (a 1ª cor no filamento de maior gramatura).
            </p>
          </div>

          {vars.length > 0 && (
            <ul className="space-y-3">
              {vars.map((v, idx) => (
                <li
                  key={v.id || `new-${idx}`}
                  className="space-y-3 rounded-md border border-white/10 bg-card/60 p-3"
                  data-testid={`product-variation-row-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <ImageUpload
                      value={v.photo_path}
                      onChange={(p) =>
                        setVars((prev) => prev.map((x, i) => (i === idx ? { ...x, photo_path: p } : x)))
                      }
                      testid={`product-variation-image-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => setVars((prev) => prev.filter((_, i) => i !== idx))}
                      data-testid={`product-variation-remove-${idx}`}
                      className="ml-auto text-muted-foreground transition-colors hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Nome da combinação">
                      <Input
                        value={v.name}
                        onChange={(e) =>
                          setVars((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x))
                          )
                        }
                        placeholder="Ex.: Rosa + Branco"
                        data-testid={`product-variation-name-${idx}`}
                      />
                    </Field>
                    <Field label="SKU" hint="Gerado automaticamente ao salvar.">
                      <Input
                        value={v.sku}
                        readOnly
                        disabled
                        placeholder="Gerado ao salvar"
                        className="font-num"
                        data-testid={`product-variation-sku-${idx}`}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2 rounded-md border border-white/10 bg-secondary/30 p-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Cores desta combinação ({v.colors.length}/4)
                    </p>
                    {v.colors.map((c, ci) => (
                      <div key={ci} className="flex items-end gap-2" data-testid={`variation-${idx}-color-${ci}`}>
                        <Field label={ci === 0 ? "Cor principal" : `Cor ${ci + 1}`} className="flex-1">
                          <Input
                            value={c.name}
                            onChange={(e) =>
                              setVars((prev) =>
                                prev.map((x, i) =>
                                  i === idx
                                    ? {
                                        ...x,
                                        colors: x.colors.map((y, j) =>
                                          j === ci ? { ...y, name: e.target.value } : y
                                        ),
                                      }
                                    : x
                                )
                              )
                            }
                            placeholder="Ex.: Rosa"
                            data-testid={`variation-${idx}-color-name-${ci}`}
                          />
                        </Field>
                        <div className="flex-1">
                          <NativeSelect
                            value={c.filament_id}
                            onChange={(value) =>
                              setVars((prev) =>
                                prev.map((x, i) =>
                                  i === idx
                                    ? {
                                        ...x,
                                        colors: x.colors.map((y, j) =>
                                          j === ci ? { ...y, filament_id: value } : y
                                        ),
                                      }
                                    : x
                                )
                              )
                            }
                            placeholder="Escolha o filamento"
                            data-testid={`variation-${idx}-color-filament-${ci}`}
                            options={(filaments || []).map((f) => ({
                              value: f.id,
                              label: `${f.material} ${f.color_name}${f.brand ? ` · ${f.brand}` : ""}`,
                            }))}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setVars((prev) =>
                              prev.map((x, i) =>
                                i === idx
                                  ? { ...x, colors: x.colors.filter((_, j) => j !== ci) }
                                  : x
                              )
                            )
                          }
                          data-testid={`variation-${idx}-color-remove-${ci}`}
                          className="mb-2 text-muted-foreground transition-colors hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="soft"
                      disabled={v.colors.length >= 4}
                      onClick={() =>
                        setVars((prev) =>
                          prev.map((x, i) =>
                            i === idx
                              ? { ...x, colors: [...x.colors, { name: "", filament_id: "" }] }
                              : x
                          )
                        )
                      }
                      data-testid={`variation-${idx}-add-color-btn`}
                      className="h-8 rounded-full px-3 text-[11px]"
                    >
                      <Plus className="mr-1 h-3 w-3" /> Adicionar cor
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-xs">
                      <Switch
                        checked={v.active !== false}
                        onCheckedChange={(value) =>
                          setVars((prev) => prev.map((x, i) => (i === idx ? { ...x, active: value } : x)))
                        }
                        data-testid={`product-variation-active-${idx}`}
                      />
                      Ativa
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <Switch
                        checked={v.show_in_catalog !== false}
                        onCheckedChange={(value) =>
                          setVars((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, show_in_catalog: value } : x))
                          )
                        }
                        data-testid={`product-variation-catalog-${idx}`}
                      />
                      No catálogo
                    </label>
                    {v.id && (
                      <span className="font-num text-xs text-muted-foreground">
                        {Number(v.stock_units || 0).toLocaleString("pt-BR")} un em estoque
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Button
            type="button"
            variant="soft"
            onClick={() =>
              setVars((prev) => [
                ...prev,
                { id: null, name: "", sku: "", photo_path: null, colors: [{ name: "", filament_id: "" }], active: true, show_in_catalog: true },
              ])
            }
            data-testid="product-add-variation-btn"
            className="h-9 rounded-full px-4 text-xs"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Adicionar combinação
          </Button>
        </div>

        <div className="space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Catálogo</p>
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">
              Exibir este produto no catálogo
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Desligado, ele continua no sistema para produção, custo, Plates e kits.
              </span>
            </span>
            <Switch
              checked={form.show_in_catalog !== false}
              onCheckedChange={(v) => setForm((f) => ({ ...f, show_in_catalog: v }))}
              data-testid="product-show-in-catalog-switch"
            />
          </label>
          <label className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
            <span className="text-sm">
              Destaque no catálogo
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Aparece na seção "Destaques" da loja, na frente da ordenação automática.
              </span>
            </span>
            <Switch
              checked={form.featured === true}
              onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))}
              data-testid="product-featured-switch"
            />
          </label>
          <label className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
            <span className="text-sm">Pode ser usado em kits</span>
            <Switch
              checked={form.kit_eligible !== false}
              onCheckedChange={(v) => setForm((f) => ({ ...f, kit_eligible: v }))}
              data-testid="product-kit-eligible-switch"
            />
          </label>
          <label className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
            <span className="text-sm">Produto ativo</span>
            <Switch
              checked={form.active !== false}
              onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
              data-testid="product-active-switch"
            />
          </label>
          <label className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
            <span className="text-sm">
              Produto permite personalização
              <span className="mt-0.5 block text-xs text-muted-foreground">
                O cliente escolhe cores, nome e opções no catálogo. Não cria combinação comercial.
              </span>
            </span>
            <Switch
              checked={customizable}
              onCheckedChange={setCustomizable}
              data-testid="product-customizable-switch"
            />
          </label>
          {customizable && (
            <div className="space-y-3 border-t border-white/10 pt-3">
              <label className="flex items-center justify-between gap-3">
                <span className="text-sm">
                  Exigir aprovação da personalização
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    O pedido só vai para produção depois que o cliente aprovar pelo link público.
                  </span>
                </span>
                <Switch
                  checked={requireApproval}
                  onCheckedChange={setRequireApproval}
                  data-testid="product-require-approval-switch"
                />
              </label>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Campos deste produto
                </p>
                {library.length === 0 ? (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Crie os campos em Configurações → Campos de personalização.
                  </p>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {library.map((f) => {
                      const on = customFields.some((x) => x.name === f.name);
                      return (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => toggleLibraryField(f)}
                          data-testid={`product-custom-field-${f.id}`}
                          className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                            on
                              ? "border-brand bg-brand/15 text-brand"
                              : "border-white/15 text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {f.name} · {typeLabel(f.type)}
                        </button>
                      );
                    })}
                  </div>
                )}
                {customFields.length > 0 && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Na ordem: {customFields.map((f) => f.name).join(" · ")}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <Button type="submit" disabled={busy} data-testid="save-product-btn" className="h-11 rounded-full font-semibold transition-transform active:scale-95">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
        </Button>
      </form>
    </ResponsiveModal>
  );
}

function ProductPlatesModal({ product, onClose, onNewPlate, onEditPlate }) {
  const [plates, setPlates] = useState(null);
  const [sendPlate, setSendPlate] = useState(null);

  useEffect(() => {
    api
      .get(`/products/${product.id}/plates`)
      .then(({ data }) => setPlates(data))
      .catch((e) => toast.error(apiError(e)));
  }, [product.id]);

  return (
    <>
    {sendPlate && (
      <SendToProductionModal
        product={product}
        plate={sendPlate}
        onClose={() => setSendPlate(null)}
      />
    )}
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Plates de ${product.name}`}
      description="A Plate é a receita de produção deste produto: quantidade por execução, tempo e filamentos. Um produto pode ter várias."
      testid="product-plates-modal"
    >
      <div className="mt-2 space-y-3">
        {!plates && <Loading label="Carregando Plates..." />}
        {plates && plates.length === 0 && (
          <p className="rounded-lg border border-dashed border-white/15 px-4 py-6 text-center text-sm text-muted-foreground" data-testid="product-plates-empty">
            Este produto ainda não tem Plate. Sem Plate ele não pode ser produzido.
          </p>
        )}
        {(plates || []).map((pl) => (
          <div
            key={pl.id}
            className="flex flex-col gap-2 rounded-lg border border-white/10 bg-secondary/40 p-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`product-plate-row-${pl.id}`}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{pl.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {pl.plate_type === "COMPONENTE" ? "Peça / componente" : "Produto completo"} ·{" "}
                {pl.output_quantity} un por execução ·{" "}
                {pl.print_time_min ? `${Math.round(pl.print_time_min)} min` : "sem tempo"} ·{" "}
                {Number(pl.total_g || 0).toLocaleString("pt-BR")} g
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-1">
              <Button
                size="sm"
                onClick={() => setSendPlate(pl)}
                data-testid={`send-to-production-btn-${pl.id}`}
                className="h-8 rounded-full px-3 text-xs font-semibold"
              >
                <Factory className="mr-1.5 h-3.5 w-3.5" />
                {pl.plate_type === "COMPONENTE" ? "Produzir esta bandeja" : "Enviar para produção"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditPlate(pl)}
                data-testid={`product-plate-edit-btn-${pl.id}`}
                className="h-8 rounded-full px-3 text-xs"
              >
                Abrir
              </Button>
            </div>
          </div>
        ))}

        {plates && plates.length > 0 && !plates.some((pl) => pl.plate_type !== "COMPONENTE") && (
          <div className="space-y-2 rounded-lg border border-white/10 bg-secondary/40 p-3" data-testid="product-assembly-send">
            <p className="text-xs text-muted-foreground">
              Produza uma bandeja por vez acima. Com as peças prontas no estoque, monte as unidades
              aqui — a montagem consome as peças e o produto entra no estoque acabado.
            </p>
            <Button
              onClick={() => setSendPlate({ id: null, assembly: true })}
              data-testid="send-to-production-assembly-btn"
              className="h-10 w-full rounded-full text-xs font-semibold"
            >
              <Factory className="mr-1.5 h-3.5 w-3.5" /> Montar unidades
            </Button>
          </div>
        )}

        <Button onClick={onNewPlate} data-testid="product-new-plate-btn" className="h-11 w-full rounded-full font-semibold">
          <Plus className="mr-2 h-4 w-4" /> Nova Plate para este produto
        </Button>
      </div>
    </ResponsiveModal>
    </>
  );
}

function SendToProductionModal({ product, plate, onClose }) {
  const assembly = !plate.id;
  const component = plate.plate_type === "COMPONENTE";
  const variations = (product.variations || []).filter((v) => v.active !== false);
  const [variationId, setVariationId] = useState(variations[0]?.id || "");
  const [quantity, setQuantity] = useState(String(plate.output_quantity || 1));
  const [plateId, setPlateId] = useState(plate.id || "");
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(false);

  const desired = Math.max(Number(quantity) || 0, 0);

  useEffect(() => {
    if (!desired || assembly || component) return;
    api
      .get(`/products/${product.id}/plate-options`, {
        params: { quantity: desired, variation_id: variationId || undefined },
      })
      .then(({ data }) => {
        setOptions(data);
        setPlateId((current) => {
          const ok = data.options.find(
            (o) => o.plate_id === current && o.variation_compatible !== false
          );
          return ok ? current : data.recommended_plate_id || "";
        });
      })
      .catch((e) => toast.error(apiError(e)));
  }, [product.id, desired, assembly, component, variationId]);

  const perRun = Number(plate.output_quantity || 1);
  const runs = component && desired ? Math.ceil(desired / perRun) : 0;
  const componentPreview = component
    ? {
        plate_name: plate.name,
        output_quantity: perRun,
        runs,
        produced: runs * perRun,
        excess: runs * perRun - desired,
        total_time_min: runs * Number(plate.print_time_min || 0),
      }
    : null;

  const chosen = componentPreview || options?.options.find((o) => o.plate_id === plateId);
  const recommended = options?.recommended_plate_id;

  const confirm = async () => {
    if (!desired) return toast.error("Informe a quantidade que você quer produzir.");
    if (variations.length && !variationId)
      return toast.error("Escolha a combinação que será produzida.");
    if (chosen && chosen.variation_compatible === false)
      return toast.error(
        "Esta Plate não é compatível com a combinação escolhida. Ajuste a Plate ou escolha outra."
      );
    setBusy(true);
    try {
      await api.post("/productions", {
        product_id: product.id,
        variation_id: variationId || null,
        quantity_needed: desired,
        plate_id: plateId || null,
        kind: assembly ? "MONTAGEM" : undefined,
        purpose: "ESTOQUE",
      });
      toast.success(
        assembly
          ? "Montagem criada. Ela já está na fila de Produção."
          : component
          ? "Bandeja enviada para produção."
          : "Produção para estoque criada. Ela já está na fila de Produção."
      );
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={assembly ? "Montar unidades" : component ? "Produzir bandeja" : "Enviar para produção"}
      description={
        assembly
          ? "A montagem consome as peças já produzidas e coloca o produto no estoque acabado."
          : component
          ? "Produz só esta bandeja de peças. As peças ficam no estoque de peças até você montar."
          : "Produção para formar estoque: não cria pedido, não cria venda e não gera receita."
      }
      testid="send-to-production-modal"
    >
      <div className="mt-2 space-y-4">
        <div className="rounded-lg border border-white/10 bg-secondary/40 p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Produto</p>
          <p className="text-sm font-medium" data-testid="send-production-product">{product.name}</p>
        </div>

        {variations.length > 0 && (
          <Field label="Combinação a produzir" hint="A produção usa os filamentos das cores desta combinação.">
            <NativeSelect
              value={variationId}
              onChange={setVariationId}
              data-testid="send-production-variation-select"
              options={variations.map((v) => ({
                value: v.id,
                label: `${v.name}${v.filament_label ? ` · ${v.filament_label}` : ""}`,
              }))}
            />
          </Field>
        )}

        <Field label={component ? "Quantidade de peças" : "Quantidade desejada para estoque"}>
          <Input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="font-num"
            data-testid="send-production-qty"
          />
        </Field>

        {options && options.options.length > 1 && !component && (
          <Field label="Plate" hint="A recomendação considera excedente, tempo e custo">
            <NativeSelect
              value={plateId}
              onChange={setPlateId}
              data-testid="send-production-plate-select"
              options={options.options.map((o) => ({
                value: o.plate_id,
                label: `${o.plate_name} · ${o.output_quantity} un/execução${
                  o.variation_compatible === false
                    ? " (incompatível com a combinação)"
                    : o.plate_id === recommended
                    ? " (recomendada)"
                    : ""
                }`,
              }))}
            />
          </Field>
        )}

        {assembly ? (
          <p className="rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs text-muted-foreground" data-testid="send-production-assembly-note">
            A montagem só pode ser concluída quando as peças estiverem no estoque de peças. Você
            verá o que falta na tela de Produção. Destino: estoque de produtos acabados — sem
            pedido e sem venda.
          </p>
        ) : chosen ? (
          <div className="space-y-2 rounded-lg border border-white/10 bg-secondary/40 p-4" data-testid="send-production-preview">
            {plateId !== recommended && recommended && !component && (
              <p className="text-xs text-amber-300">
                Plate recomendada: {options.options.find((o) => o.plate_id === recommended)?.plate_name}
              </p>
            )}
            <Row label="Plate" value={chosen.plate_name} />
            <Row label="Produz por execução" value={`${chosen.output_quantity} un`} />
            <Row label="Execuções necessárias" value={`${chosen.runs}`} testid="send-production-runs" />
            <Row label="Total produzido" value={`${chosen.produced} un`} testid="send-production-total" />
            <Row label="Excedente" value={`${chosen.excess} un`} testid="send-production-excess" />
            <Row label="Tempo estimado" value={`${Math.round(chosen.total_time_min)} min`} />
            {chosen.estimated_cost !== undefined && (
              <Row label="Custo estimado" value={brl(chosen.estimated_cost)} testid="send-production-cost" />
            )}
            <Row
              label="Destino"
              value={component ? "Estoque de peças" : "Estoque de produtos acabados"}
            />
            {chosen.variation_compatible === false && (
              <p className="text-xs text-red-300" data-testid="send-production-incompatible-warning">
                Esta Plate não é compatível com a combinação escolhida: a Plate usa{" "}
                {chosen.max_colors} filamento(s). Ajuste a Plate ou escolha outra.
              </p>
            )}
            {chosen.filaments_available === false && (
              <p className="text-xs text-red-300" data-testid="send-production-filament-warning">
                Atenção: falta filamento para esta Plate. A produção é criada, mas você não
                conseguirá concluí-la sem repor o estoque.
              </p>
            )}
          </div>
        ) : (
          <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="send-production-no-plate">
            Informe a quantidade para ver a recomendação de Plate.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="send-production-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={confirm} disabled={busy || (!chosen && !assembly) || chosen?.variation_compatible === false} data-testid="send-production-confirm-btn" className="h-11 rounded-full px-6 font-semibold">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Criar produção
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-num" data-testid={testid}>{value}</span>
  </div>
);
